import { type ReactNode, type RefObject, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface MobileNavigationModalBoundaryProps {
  active: boolean;
  children: ReactNode;
  initialFocusRef: RefObject<HTMLElement>;
  onRequestClose: () => void;
}

interface PreservedElementState {
  hadInert: boolean;
  ariaHidden: string | null;
}

function isolateDocumentOutsideModal(
  modal: HTMLDialogElement,
  restoreFocusInside: () => void,
): () => void {
  const preserved = new Map<HTMLElement, PreservedElementState>();

  const isolate = (element: HTMLElement) => {
    if (element === modal || element.contains(modal) || preserved.has(element)) return;

    preserved.set(element, {
      hadInert: element.hasAttribute('inert'),
      ariaHidden: element.getAttribute('aria-hidden'),
    });
    element.setAttribute('inert', '');
    element.setAttribute('aria-hidden', 'true');
  };

  const isolateBodyChildren = () => {
    for (const child of Array.from(document.body.children)) {
      if (child instanceof HTMLElement) isolate(child);
    }
  };

  isolateBodyChildren();

  // Third-party launchers such as Intercom can be appended after the drawer
  // opens. Keep every new body sibling inside the same modal boundary.
  const bodyObserver = new MutationObserver(isolateBodyChildren);
  bodyObserver.observe(document.body, { childList: true });

  const blockOutsideInteraction = (event: Event) => {
    const target = event.target;
    if (!(target instanceof Node) || modal.contains(target) || target === modal) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    if (event.type === 'focusin') restoreFocusInside();
  };

  const blockedEvents = ['pointerdown', 'mousedown', 'touchstart', 'click', 'focusin'] as const;
  for (const eventName of blockedEvents) {
    document.addEventListener(eventName, blockOutsideInteraction, { capture: true, passive: false });
  }

  return () => {
    bodyObserver.disconnect();
    for (const eventName of blockedEvents) {
      document.removeEventListener(eventName, blockOutsideInteraction, true);
    }
    for (const [element, state] of preserved) {
      if (state.hadInert) element.setAttribute('inert', '');
      else element.removeAttribute('inert');

      if (state.ariaHidden === null) element.removeAttribute('aria-hidden');
      else element.setAttribute('aria-hidden', state.ariaHidden);
    }
  };
}

/**
 * Keeps the mobile navigation in the browser's top layer while making every
 * other app or body-injected surface non-interactive. Inactive children remain
 * inline so the same navigation tree is used for the desktop sidebar.
 */
export function MobileNavigationModalBoundary({
  active,
  children,
  initialFocusRef,
  onRequestClose,
}: MobileNavigationModalBoundaryProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useLayoutEffect(() => {
    if (!active) return;

    const dialog = dialogRef.current;
    if (!dialog) return;

    if (typeof dialog.showModal === 'function') {
      try {
        if (!dialog.open) dialog.showModal();
      } catch {
        // Keep the accessibility and interaction boundary intact in browsers
        // that expose <dialog> without a functioning top-layer implementation.
        dialog.setAttribute('open', '');
      }
    } else {
      dialog.setAttribute('open', '');
    }

    const focusInside = () => initialFocusRef.current?.focus();
    focusInside();
    const releaseIsolation = isolateDocumentOutsideModal(dialog, focusInside);
    const focusTimer = window.setTimeout(focusInside, 0);

    return () => {
      window.clearTimeout(focusTimer);
      releaseIsolation();
      if (dialog.open && typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    };
  }, [active, initialFocusRef]);

  if (!active || typeof document === 'undefined') return children;

  return createPortal(
    <dialog
      ref={dialogRef}
      aria-labelledby="mobile-navigation-title"
      aria-modal="true"
      className="fixed inset-0 m-0 h-dvh max-h-none w-screen max-w-none overflow-hidden border-0 bg-transparent p-0 text-inherit backdrop:bg-black/50"
      onCancel={(event) => {
        event.preventDefault();
        onRequestClose();
      }}
      onKeyDown={(event) => {
        // Let navigation controls receive keys, but do not let their events
        // reach app-wide shortcut handlers behind the modal.
        event.stopPropagation();
      }}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onRequestClose();
      }}
    >
      {children}
    </dialog>,
    document.body,
  );
}
